import React, { useState, useContext, useEffect, useId } from 'react';
import { TextButton, SvgButton, Spinner, A } from 't-lib-ui/widgets';
import { sanitizePlainText, sanitizeLongText } from 't-lib-ui/connector';
import { HelpPopup, getKeyHandler } from 't-lib-ui/structure';
import { RouterContext, goTo } from 't-lib-ui/router';
import { RestCatalog, RestFeedSummary, RestUserFeed } from '../schema-copy/api-schema';
import { getCachedFeedSummaries } from '../connector/feed-summaries';
import { nonNull } from 't-lib-ui/helpers';
import { formatPostAge, formatFrequency, formatCategory } from '../utils/formatting';
import { AppState } from '../context/general';
import { CategoryIcon } from './CategoryIcons';
import { useFeedNameOverride } from '../connector/feed-name-overrides';
import CheckmarkCircleIcon from './CheckmarkCircleIcon';
import FeedInfoPopup from './FeedInfoPopup';
import FlagIcon from './FlagIcon';
import { useReport } from './ReportPopup';
import PrivateFeedIcon from './PrivateFeedIcon';
import MailIcon from './MailIcon';
import FeedIssueIcon from './FeedIssueIcon';
import FeedIssuePopup from './FeedIssuePopup';
import { hasFeedIssue } from '../utils/feedIssue';
import { postStatsBadges, postTextBadge } from '../schema-copy/post-stats-badges';
import { PostTextType } from '../schema-copy/feed-schema';
import FeedIcon from './FeedIcon';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import { getDefaultIconUrl } from '../schema-copy/default-icon';
import { requestStartCollapsed } from './ThreeColumnLayout';
import { useFeedQuota } from '../connector/feed-quota';
import FeedQuotaNote from './FeedQuotaNote';
import AdminCategoryPopup from './admin/AdminCategoryPopup';
import AdminTextTypePopup from './admin/AdminTextTypePopup';
import { useFolderPicker } from './FolderPickerPopup';
import PauseIcon from './PauseIcon';
import './FeedTile.css';

/** Posts per day from which the frequency pill is set in bold. */
const HIGH_FREQUENCY_POSTS_PER_DAY = 10;

interface FeedTileProps {
  feed: RestUserFeed | RestCatalog;
  subscribed: boolean;
  onAction?: () => void;
  onView?: () => void;
  onQuotaExceeded?: () => void;
  showPrivacyNote?: boolean;
  isAdmin?: boolean;
  /** Leave out the category badge (in a category's own list, where every feed shares it). Admins
   *  still get it, as it is their button to change the category. */
  hideCategory?: boolean;
  /** No "View" button once followed: the tile already sits on top of the feed's posts. */
  hideView?: boolean;
  children?: React.ReactNode;
  /** Extra badges in their own row below the info badges (e.g. the feed's limits/filter/pause). */
  extraBadges?: React.ReactNode;
  /** The followed feed is paused: dims the image and shows a pause icon next to "Unfollow". */
  paused?: boolean;
}

const FeedTile: React.FC<FeedTileProps> = ({ feed, subscribed, onAction, onView, onQuotaExceeded, showPrivacyNote = false, isAdmin = false, hideCategory = false, hideView = false, children, extraBadges, paused = false }) => {
  const appState = useContext(AppState);
  const { feedEngine } = appState;
  const routerCtx = useContext(RouterContext);
  const customFeedName = useFeedNameOverride(feed.id);
  const { report, reportPopup } = useReport();
  const askFolder = useFolderPicker();
  const { atLimit: atFeedQuota } = useFeedQuota();
  // Block following a new feed once the quota is reached; unfollowing is always allowed.
  const followBlocked = atFeedQuota && !subscribed;

  // Children/parent are only present on catalog entries
  const catalog = feed.objectType == 'catalog' ? feed as RestCatalog : null;
  const childIds = catalog?.children;
  const parentId = catalog?.parent;

  const [groupSummaries, setGroupSummaries] = useState<Record<string, RestFeedSummary>>({});

  useEffect(() => {
    const ids = [...(childIds ?? []), ...(parentId ? [parentId] : [])];
    if (!ids.length || !appState.state.authData) {
      setGroupSummaries({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const summaries = await getCachedFeedSummaries(nonNull(appState.state.authData), ids.slice(0, 31));
        if (cancelled) return;
        const map: Record<string, RestFeedSummary> = {};
        for (const s of summaries) map[s.id] = s;
        setGroupSummaries(map);
      } catch (e) {
        if (!cancelled) console.error('Failed to load feed group summaries:', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childIds?.join(','), parentId, appState.state.authData]);

  const childrenForDisplay = childIds?.map(id => groupSummaries[id]).filter((s): s is RestFeedSummary => !!s) ?? [];
  const parentSummary = parentId ? groupSummaries[parentId] : undefined;

  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [showPaidInfoPopup, setShowPaidInfoPopup] = useState(false);
  const [showPrivateInfoPopup, setShowPrivateInfoPopup] = useState(false);
  const [showIssuePopup, setShowIssuePopup] = useState(false);
  const [showDelayedPopup, setShowDelayedPopup] = useState(false);
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  // Set after an admin changed the category, so the tile reflects it without reloading the list.
  const [categoryOverride, setCategoryOverride] = useState<string | undefined>(undefined);
  const [showTextTypePopup, setShowTextTypePopup] = useState(false);
  // Same for the text type; wrapped because undefined (back to auto) is a valid new value.
  const [textTypeOverride, setTextTypeOverride] = useState<{ textType?: PostTextType } | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [imgErrored, setImgErrored] = useState(false);
  const infoButtonId = `feed-info-btn-${feed.id}`;
  const paidButtonId = `feed-paid-btn-${feed.id}`;
  const privateButtonId = `feed-private-btn-${feed.id}`;
  const issueButtonId = `feed-issue-btn-${feed.id}`;
  const delayedButtonId = `feed-delayed-btn-${feed.id}`;
  const adminButtonId = `feed-admin-btn-${feed.id}`;
  const reportButtonId = `feed-report-btn-${feed.id}`;
  // Unique per tile: the same feed can show up in several tile lists on one page.
  const followButtonId = `feed-follow-btn-${useId()}`;

  const handleActionClick = async () => {
    if (!feedEngine || isProcessing) return;

    // Clear any previous error
    setErrorMessage(null);
    setIsProcessing(true);

    try {
      const minDelayPromise = new Promise(resolve => setTimeout(resolve, 750));

      // Check feed engine state
      const engineState = feedEngine.getState();
      if (engineState != 'READY' && engineState != 'UPDATING' && engineState != 'INTERRUPTED') {
        await minDelayPromise;
        if (engineState == 'LOADING') {
          setErrorMessage('Please wait a moment and try again.');
        } else if (engineState == 'ERROR') {
          setErrorMessage('Failed to load your feeds. Please refresh the page.');
        } else {
          setErrorMessage(`Unable to follow. Please try again. (Feed engine state: ${engineState})`);
        }
        return;
      }

      // Perform the operation
      let success = false;
      if (subscribed) {
        success = await feedEngine.unsubscribe(feed.id);
      } else {
        // Hand the engine this catalog feed's metadata so it can subscribe without a server lookup
        // (essential for the anonymous engine, which otherwise can't resolve an unsubscribed feed).
        feedEngine.registerFeedInfo(feed);
        // The follow starts right away; the folder question runs alongside it.
        const subscription = feedEngine.subscribe(feed.id);
        askFolder({ feedIds: [feed.id], subscription, subject: customFeedName ?? feed.shortTitle ?? feed.title, anchorId: followButtonId });
        success = !!await subscription;
      }

      await minDelayPromise;

      if (success) {
        onAction?.();
      } else {
        setErrorMessage(subscribed ? 'Failed to unfollow. Please try again.' : 'Failed to follow. Please try again.');
      }
    } catch (error) {
      if (error && typeof error == 'object' && 'type' in error && (error as any).type == 'Quota') {
        onQuotaExceeded?.();
      } else {
        console.error('Subscription action failed:', error);
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        setErrorMessage(`Error: ${errorMsg}`);
      }

      // Wait for minimum delay even on error
      await new Promise(resolve => setTimeout(resolve, 750));
    } finally {
      setIsProcessing(false);
    }
  };

  const isCatalog = feed.objectType == 'catalog';
  const category = categoryOverride ?? feed.category;
  const lastPostAge = formatPostAge(feed.lastNewPostPubDate);
  const textType = textTypeOverride ? textTypeOverride.textType : catalog?.textType;
  const textBadge = postTextBadge(feed.postStats, category, textType);
  // Admins edit the text badge on catalog entries (where the override lives), so it is shown apart.
  const editTextType = isAdmin && isCatalog;
  const badges = postStatsBadges(feed.postStats, category, textType).filter(b => !editTextType || b != textBadge);

  // Drop stale local overrides if the tile gets recycled for another feed.
  useEffect(() => {
    setCategoryOverride(undefined);
    setTextTypeOverride(undefined);
  }, [feed.id]);

  // The selected feed image (favicon / feed image / custom) at 96px-rendered (192px for retina),
  // falling back to the generated icon both when no image exists and if the CDN image fails to load.
  const imageUrl = imgErrored ? getDefaultIconUrl(feed.shortTitle ?? feed.title) : getFeedIconUrl(feed, 192);
  const siteHostname = (() => {
    if (!feed.siteUrl) return '';
    try {
      return new URL(feed.siteUrl).hostname;
    } catch {
      return feed.siteUrl;
    }
  })();

  return (
    <div className="feedTile">
      <div className="feedTileHeader withImage">
        <div className={`feedTileImageWrap${paused ? ' paused' : ''}`}>
          <img
            src={imageUrl}
            alt={feed.title}
            className={`feedTileImage${feed.circularIcon ? ' feedIconCircular' : ''}`}
            onError={() => { if (!imgErrored) setImgErrored(true); }}
          />
        </div>
        <div className="feedTileHeaderBody">
          <div className="feedTileHeaderTop">
            <div className="feedTileHeaderText">
              <h3 className="feedTileTitle">{customFeedName ?? feed.shortTitle ?? feed.title}</h3>
              {feed.siteUrl && (
                <p className="feedTileUrl">
                  <A href={feed.siteUrl} target="_blank" rel="noopener noreferrer">
                    {siteHostname}
                  </A>
                </p>
              )}
            </div>
            <div className="feedTileHeaderTools">
              {feed.feedStatus == 'NEWSLETTER' && (
                <span className="feedTilePrivateLock" title="Newsletter feed" style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <MailIcon size={22} color="currentColor" />
                </span>
              )}
              {feed.feedStatus == 'PRIVATE' && (
                <SvgButton
                  focusId={privateButtonId}
                  className="feedTilePrivateLock"
                  onClick={() => setShowPrivateInfoPopup(true)}
                  title="Private feed"
                  type="minor"
                  size="medium"
                >
                  <PrivateFeedIcon size={22} color="var(--error-color)" />
                </SvgButton>
              )}
              {hasFeedIssue(feed) && (
                <SvgButton
                  focusId={issueButtonId}
                  className="feedTilePrivateLock"
                  onClick={() => setShowIssuePopup(true)}
                  title="This feed has a problem"
                  type="minor"
                  size="medium"
                >
                  <FeedIssueIcon size={20} color="var(--error-color)" />
                </SvgButton>
              )}
              {feed.residentialProxy && !hasFeedIssue(feed) && (
                <SvgButton
                  focusId={delayedButtonId}
                  className="feedTilePrivateLock"
                  onClick={() => setShowDelayedPopup(true)}
                  title="Updates for this feed are delayed"
                  type="minor"
                  size="medium"
                >
                  <FeedIssueIcon size={20} color="var(--warning-color)" />
                </SvgButton>
              )}
              {isAdmin && (
                <TextButton
                  focusId={adminButtonId}
                  onClick={() => window.open(isCatalog ? `/a/admin/catalog/${feed.id}` : `/a/admin/feed/${feed.id}`, '_blank', 'noopener')}
                  label="A"
                  className='adminButton'
                  size='small'
                  type='borderless'
                />
              )}
              {feed.paid === true && (
                <TextButton
                  focusId={paidButtonId}
                  onClick={() => setShowPaidInfoPopup(true)}
                  label="$"
                  className='dollarButton'
                  size='small'
                  type='borderless'
                />
              )}
              <SvgButton
                focusId={reportButtonId}
                className="feedTileReportIcon"
                onClick={() => report({ feedId: feed.id, title: feed.shortTitle ?? feed.title })}
                title="Report this feed"
                type="minor"
                size="medium"
              >
                <FlagIcon size={18} />
              </SvgButton>
              <SvgButton
                focusId={infoButtonId}
                className="feedTileInfoIcon"
                onClick={() => setShowInfoPopup(true)}
                title="Show feed details"
                type="minor"
                size="medium"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <circle cx="12" cy="8" r="0.5" fill="currentColor"/>
                </svg>
              </SvgButton>
            </div>
          </div>
          {(feed.description || ('catalogDescription' in feed && feed.catalogDescription)) && (
            <div className="feedTileDescriptions">
              {feed.description && (
                <p className="feedTileDescription">
                  {sanitizeLongText(sanitizePlainText(feed.description), 200)}
                </p>
              )}
              {'catalogDescription' in feed && feed.catalogDescription && (
                <p className="feedTileCatalogDescription">
                  {feed.catalogDescription}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <HelpPopup
        show={showPaidInfoPopup}
        title="Paid Feeds"
        onClose={() => setShowPaidInfoPopup(false)}
        closable={true}
        closeFocus={paidButtonId}
        className="feedTilePaidInfoPopup"
      >
        <div>
          This site charges for access to most content. Adding the feed to Downread is free, 
          and you can browse article titles and summaries at no cost.
        </div>
        <div style={{ marginTop: '12px' }}>
          However, to read the full articles, you may need to subscribe directly on the 
          publisher's website. Downread does not charge for accessing feeds – any subscription 
          fees are paid directly to the content publisher.
        </div>
      </HelpPopup>

      <HelpPopup
        show={showPrivateInfoPopup}
        title="Private Feed"
        onClose={() => setShowPrivateInfoPopup(false)}
        closable={true}
        closeFocus={privateButtonId}
      >
        <div>
          This is a private feed. It will not be published or shown to other users.
        </div>
        <div style={{ marginTop: '12px' }}>
          Only users who know the exact feed URL can add it to their own account.
          Private feeds are intended for personal subscriptions such as paid newsletters
          or feeds with personalized content.
        </div>
      </HelpPopup>

      <FeedIssuePopup
        show={showIssuePopup}
        fetchErrorCount={feed.fetchErrorCount}
        fetchFailingSinceAt={feed.fetchFailingSinceAt}
        feedTitle={feed.shortTitle ?? feed.title}
        closeFocus={issueButtonId}
        onClose={() => setShowIssuePopup(false)}
      />

      <HelpPopup
        show={showDelayedPopup}
        title="Delayed Updates"
        onClose={() => setShowDelayedPopup(false)}
        closable={true}
        closeFocus={delayedButtonId}
      >
        <div>
          This feed's website refuses our regular requests. It is probably misconfigured, or
          protected by a bot blocker that does not let feed readers through.
        </div>
        <div style={{ marginTop: '12px' }}>
          We can still fetch it through a slower fallback route, but only about once a day, so new
          posts may show up with a delay of up to 24 hours. If the site fixes the problem, updates
          return to normal automatically.
        </div>
      </HelpPopup>

      <FeedInfoPopup
        feed={showInfoPopup ? feed : null}
        onClose={() => setShowInfoPopup(false)}
        closeFocus={infoButtonId}
      />

      {reportPopup}

      {isAdmin && (
        <AdminCategoryPopup
          show={showCategoryPopup}
          feedId={feed.id}
          isCatalog={isCatalog}
          category={category}
          onClose={() => setShowCategoryPopup(false)}
          onChanged={setCategoryOverride}
        />
      )}

      {editTextType && (
        <AdminTextTypePopup
          show={showTextTypePopup}
          feedId={feed.id}
          textType={textType}
          detected={postTextBadge(feed.postStats, category)}
          onClose={() => setShowTextTypePopup(false)}
          onChanged={t => setTextTypeOverride({ textType: t })}
        />
      )}

      {feed.downreadPublicComment && (
        <p className="feedTileComment">
          {feed.downreadPublicComment}
        </p>
      )}

      <div className="feedTileMetadata">
        {/* Admins can retune the category straight from the tile; everyone else sees plain text.
            The button also shows without a category, so a cleared one can be set again. */}
        {isAdmin ? (
          <button
            type="button"
            className="feedTileMetaItem feedTileMetaCategory feedTileMetaCategoryButton flowFocussable"
            tabIndex={0}
            title="Change category"
            onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any}
            onClick={() => setShowCategoryPopup(true)}
          >
            {category && <CategoryIcon category={category} size={16} color="var(--gray-400)" />}
            {(category && (formatCategory(category) ?? category)) || 'Set category'}
          </button>
        ) : category && !hideCategory && formatCategory(category) && (
          <span className="feedTileMetaItem feedTileMetaCategory">
            <CategoryIcon category={category} size={16} color="var(--gray-400)" />
            {formatCategory(category)}
          </span>
        )}
        {(() => {
          // Only show post frequency if feed has been in catalog for at least 48h
          if (!feed.postFrequency) return null;
          // Check if this is a catalog entry (has dateAdded field)
          if (!('dateAdded' in feed)) return null;
          const dateAdded = new Date(feed.dateAdded).getTime();
          const fortyEightHoursAgo = Date.now() - (48 * 60 * 60 * 1000);
          if (dateAdded < fortyEightHoursAgo) {
            // A flood of posts is worth a warning, so it stands out.
            // The leading "about" is dropped on narrow screens (CSS) to save room on the badge line.
            const formatted = formatFrequency(feed.postFrequency);
            const frequency = formatted.startsWith('about ')
              ? <><span className="feedTileMetaAbout">about </span>{formatted.slice(6)}</>
              : formatted;
            return <span className="feedTileMetaItem">{feed.postFrequency >= HIGH_FREQUENCY_POSTS_PER_DAY ? <strong>{frequency}</strong> : frequency}</span>;
          }
          return null;
        })()}
        {/* What a typical post carries (words, foreign links, videos, audio), from the sync server's
            per-post averages unless the catalog overrides the text badge. Absent on feeds that
            haven't been analyzed yet. Admins can override the text badge of catalog entries. */}
        {editTextType && (
          <button
            type="button"
            className="feedTileMetaItem feedTileMetaCategoryButton flowFocussable"
            tabIndex={0}
            title={textType ? 'Change content type (set by admin)' : 'Change content type (auto-detected)'}
            onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any}
            onClick={() => setShowTextTypePopup(true)}
          >
            {textBadge ?? 'Set content type'}
          </button>
        )}
        {badges.map(badge => (
          <span key={badge} className="feedTileMetaItem">{badge}</span>
        ))}
        {lastPostAge && <span className="feedTileMetaItem">Last post {lastPostAge}</span>}
      </div>

      {extraBadges && (
        <div className="feedTileMetadata feedTileSettingsRow">
          {extraBadges}
        </div>
      )}

      {childrenForDisplay.length > 0 && (
        <div className="feedTileChildren">
          <div className="feedTileChildrenLabel">Collection:</div>
          <ul className="feedTileChildList">
            {childrenForDisplay.map(c => (
              <li key={c.id}>
                <button
                  type="button"
                  className="feedTileChildItem flowFocussable"
                  tabIndex={0}
                  onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any}
                  onClick={() => goTo(routerCtx, `/a/add/feed/${c.id}`)}
                >
                  <FeedIcon className="feedIcon" src={c.iconUrl} size={16} circular={c.circularIcon} fallback={<span className="feedIcon">📰</span>} />
                  <span className="feedName">{c.shortName}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {parentSummary && (
        <div className="feedTileParent">
          <span className="feedTileParentLabel">Part of </span>
          <button
            type="button"
            className="feedTileChildItem flowFocussable"
            tabIndex={0}
            onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: (el: any) => el.click() }) as any}
            onClick={() => goTo(routerCtx, `/a/add/feed/${parentSummary.id}`)}
          >
            <FeedIcon className="feedIcon" src={parentSummary.iconUrl} size={16} circular={parentSummary.circularIcon} fallback={<span className="feedIcon">📰</span>} />
            <span className="feedName">{parentSummary.shortName}</span>
          </button>
        </div>
      )}

      {onAction && (
        <div className="feedTileAction">
          <Spinner spinning={isProcessing} className={isProcessing ? 'show' : ''} />
          {errorMessage && <span className="feedTileErrorText">{errorMessage}</span>}
          {followBlocked && !errorMessage && <FeedQuotaNote />}
          <div className="feedTileActionButtons">
            {subscribed && !isProcessing && (
              <span className="feedTileSubscribedIcon feedTileSubscribedIconInline" title={paused ? 'Following, paused' : 'Following'} aria-label={paused ? 'Following, paused' : 'Following'}>
                {paused ? <PauseIcon circle size={17} /> : <CheckmarkCircleIcon size={17} color="currentColor" />}
              </span>
            )}
            <TextButton
              focusId={followButtonId}
              label={subscribed ? "Unfollow" : "+ Follow"}
              type="major"
              size="small"
              enabled={isProcessing || followBlocked ? "disabled" : "enabled"}
              onClick={handleActionClick}
              className="feedTileActionButton"
            />
            {(subscribed ? !hideView : !!onView) && (
              <TextButton
                label="View"
                type={subscribed ? 'major' : 'minor'}
                size="small"
                focussable='sideway'
                onClick={() => {
                  requestStartCollapsed();
                  feedEngine.rememberFeedHeader(feed.id, { title: feed.shortTitle || feed.title, iconUrl: getFeedIconUrl(feed, 48), circularIcon: feed.circularIcon });
                  onView?.();
                  goTo(routerCtx, `/a/feed/${feed.id}`);
                }}
                className="feedTileActionButton"
              />
            )}
          </div>
        </div>
      )}

      {children && (
        <div className="feedTileExtra">
          {children}
        </div>
      )}

    </div>
  );
};

export default FeedTile;
